// EmbeddedBoardView.js - Board view component for embedding in Dashboard
import React, { useEffect, useState, useMemo } from "react";
import { auth, db } from "./firebase.js";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  doc,
  getDocs,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Paper, Card, CardContent, Typography, Box } from "@mui/material";

export default function EmbeddedBoardView({ projectId, projectName, filterUser, fromDate, toDate }) {
  const [tasks, setTasks] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [projectMembers, setProjectMembers] = useState([]);

  // Filter state
  // const [selectedUser, setSelectedUser] = useState("All Users"); // Moved to Dashboard
  const [visibleStatuses, setVisibleStatuses] = useState(
    new Set(["Pending", "In Progress", "On Hold", "Completed"])
  );

  // Check if mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to format timestamp
  const formatTs = (ts) => {
    if (!ts) return "—";
    let date;
    if (ts.seconds) {
      date = new Date(ts.seconds * 1000);
    } else {
      date = new Date(ts);
    }
    if (isNaN(date)) return "—";
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${day} ${month}, ${time}`;
  };

  // Helper function to calculate task age in days
  const calculateTaskAge = (createdAt) => {
    if (!createdAt) return null;

    let createdDate;

    if (createdAt.seconds) {
      createdDate = new Date(createdAt.seconds * 1000);
    } else {
      createdDate = new Date(createdAt);
    }

    if (isNaN(createdDate)) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    createdDate.setHours(0, 0, 0, 0);

    const diffTime = today - createdDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day";
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} year${years !== 1 ? 's' : ''}`;
  };

  // Helper function to get current user's UID
  const getCurrentUserUID = () => {
    const user = auth.currentUser;
    return user?.uid || null;
  };

  // Fetch project members for assignment
  useEffect(() => {
    const fetchProjectMembers = async () => {
      if (!projectId) return;

      try {
        const projectDoc = await getDoc(doc(db, "projects", projectId));

        if (projectDoc.exists()) {
          const projectData = projectDoc.data();
          const userIds = projectData.users || [];

          if (userIds.length > 0) {
            const usersRef = collection(db, "users");
            const usersSnapshot = await getDocs(usersRef);

            const members = [];
            usersSnapshot.forEach((userDoc) => {
              if (userIds.includes(userDoc.id)) {
                const userData = userDoc.data();
                members.push({
                  uid: userDoc.id,
                  name: userData.username || userData.email || "Unknown User",
                  email: userData.email || ""
                });
              }
            });

            setProjectMembers(members);
          }
        }
      } catch (error) {
        console.error("Error fetching project members:", error);
      }
    };

    fetchProjectMembers();
  }, [projectId]);

  // Update task assignment
  const updateTaskAssignment = async (taskId, assignedToUID) => {
    if (!taskId) return;

    try {
      const taskRef = doc(db, "projects", projectId, "tasks", taskId);
      const updateData = {
        updatedAt: serverTimestamp(),
      };

      if (assignedToUID) {
        updateData.assignedTo = assignedToUID;
        const assignedMember = projectMembers.find(m => m.uid === assignedToUID);
        if (assignedMember) {
          updateData.assignedToName = assignedMember.name;
          updateData.assignedToEmail = assignedMember.email;
        }
      } else {
        updateData.assignedTo = null;
        updateData.assignedToName = null;
        updateData.assignedToEmail = null;
      }

      await updateDoc(taskRef, updateData);
    } catch (error) {
      console.error("Error updating task assignment:", error);
      alert(`Failed to update assignment: ${error.message || "Unknown error"}`);
    }
  };

  // Update task status
  const updateTaskStatus = async (taskId, newStatus) => {
    if (!taskId || !newStatus) return;

    try {
      const user = auth.currentUser;
      const updatedBy = user?.displayName?.trim() || user?.email || "Unknown";

      const taskRef = doc(db, "projects", projectId, "tasks", taskId);
      await updateDoc(taskRef, {
        status: newStatus,
        updatedBy: updatedBy,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating task status:", error);
      alert(`Failed to update status: ${error.message || "Unknown error"}`);
    }
  };

  // Real-time listener for tasks
  useEffect(() => {
    if (!projectId) return;

    const tasksColPath = collection(db, "projects", projectId, "tasks");
    const q = query(
      tasksColPath,
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTasks(arr);
      },
      (err) => {
        console.error("Tasks snapshot error:", err);
      }
    );

    return () => unsub();
  }, [projectId]);

  // Helper function to check if a task date is within the date range
  const isTaskInDateRange = (task) => {
    // If dates are empty, show all tasks
    if (!fromDate || !toDate || fromDate === "" || toDate === "") return true;
    
    // Get task date (use createdAt or updatedAt)
    let taskDate;
    if (task.createdAt) {
      if (task.createdAt.seconds) {
        taskDate = new Date(task.createdAt.seconds * 1000);
      } else {
        taskDate = new Date(task.createdAt);
      }
    } else if (task.updatedAt) {
      if (task.updatedAt.seconds) {
        taskDate = new Date(task.updatedAt.seconds * 1000);
      } else {
        taskDate = new Date(task.updatedAt);
      }
    } else {
      return true; // If no date, include it
    }
    
    // Convert to date only (ignore time)
    taskDate.setHours(0, 0, 0, 0);
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999); // Include entire end date
    
    return taskDate >= from && taskDate <= to;
  };

  // Filter tasks by selected user (passed from Dashboard) and date range
  const filteredTasks = useMemo(() => {
    let userFiltered = tasks;
    
    // Apply user filter
    if (filterUser && filterUser !== "All" && filterUser !== "All Users") {
      if (filterUser === "Unassigned") {
        userFiltered = tasks.filter(t => !t.assignedTo);
      } else {
        // Filter by assignedTo UID
        userFiltered = tasks.filter(t => t.assignedTo === filterUser);
      }
    }
    
    // Apply date range filter (if dates are provided and not empty)
    if (fromDate && toDate && fromDate !== "" && toDate !== "") {
      return userFiltered.filter(isTaskInDateRange);
    }
    
    // If no date filter, return all user-filtered tasks
    return userFiltered;
  }, [tasks, filterUser, fromDate, toDate]);

  // Toggle status visibility
  const toggleStatus = (status) => {
    setVisibleStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  // Filter tasks by status (case-insensitive matching)
  const pendingTasks = filteredTasks.filter(
    (t) => t.status?.toLowerCase() === "pending"
  );
  const inProgressTasks = filteredTasks.filter(
    (t) => t.status?.toLowerCase() === "in progress" || t.status?.toLowerCase() === "progress"
  );
  const onHoldTasks = filteredTasks.filter(
    (t) => t.status?.toLowerCase() === "on hold" || t.status?.toLowerCase() === "onhold"
  );
  const completedTasks = filteredTasks.filter(
    (t) => t.status?.toLowerCase() === "completed"
  );

  // Render task card
  const renderTaskCard = (task) => (
    <Paper
      key={task.id}
      elevation={0}
      style={{
        padding: "16px",
        borderRadius: "6px",
        marginBottom: "10px",
        width: "100%",
        border: "1px solid #e5e7eb",
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
        transition: "all 0.15s ease",
        cursor: "default",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.08)";
        e.currentTarget.style.borderColor = "#d1d5db";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.04)";
        e.currentTarget.style.borderColor = "#e5e7eb";
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "12px" }}>
        <div style={{
          width: "3px",
          height: "100%",
          backgroundColor: task.status === "Pending" ? "#fbbf24" :
            task.status === "In Progress" ? "#60a5fa" :
              task.status === "On Hold" ? "#f87171" : "#34d399",
          borderRadius: "2px",
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0
        }} />
        <Typography variant="body2" style={{
          fontWeight: 500,
          fontSize: "0.875rem",
          color: "#4b5563",
          lineHeight: "1.5",
          flex: 1,
          paddingLeft: "8px"
        }}>
          {task.text || "No description"}
        </Typography>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "10px" }}>
        <Typography variant="caption" color="text.secondary" style={{ fontSize: "0.75rem", color: "#6b7280" }}>
          Created By: <strong style={{ color: "#4b5563", fontWeight: 500 }}>{task.createdByName || "Unknown"}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary" style={{ fontSize: "0.75rem", color: "#6b7280" }}>
          Updated By: <strong style={{ color: "#4b5563", fontWeight: 500 }}>{task.updatedBy || "Unknown"}</strong>
        </Typography>
      </div>

      {/* Status Dropdown - Anyone can change */}
      <Typography variant="caption" color="text.secondary" display="block" style={{ marginTop: "10px", marginBottom: "6px", fontSize: "0.75rem", color: "#6b7280" }}>
        <span style={{ marginRight: "8px" }}>Status:</span>
        <select
          value={task.status || "Pending"}
          onChange={(e) => updateTaskStatus(task.id, e.target.value)}
          style={{
            fontSize: "0.75rem",
            padding: "4px 8px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            backgroundColor:
              task.status === "Completed" ? "#d1fae5" :
                task.status === "In Progress" ? "#dbeafe" :
                  task.status === "On Hold" ? "#fee2e2" : "#fef3c7",
            minWidth: "120px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s ease",
            color:
              task.status === "Completed" ? "#065f46" :
                task.status === "In Progress" ? "#1e40af" :
                  task.status === "On Hold" ? "#991b1b" : "#92400e"
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#111827";
            e.target.style.boxShadow = "0 0 0 2px rgba(17, 24, 39, 0.1)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#d1d5db";
            e.target.style.boxShadow = "none";
          }}
        >
          <option value="Pending">Pending</option>
          <option value="In Progress">In Progress</option>
          <option value="On Hold">On Hold</option>
          <option value="Completed">Completed</option>
        </select>
      </Typography>

      {/* Show Assigned To - Only for Pending tasks */}
      {task.status === "Pending" && (
        <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: "6px", fontSize: "0.75rem", color: "#6b7280" }}>
          <span style={{ marginRight: "8px" }}>Assign to:</span>
          <select
            value={task.assignedTo || ""}
            onChange={(e) => updateTaskAssignment(task.id, e.target.value || null)}
            style={{
              fontSize: "0.75rem",
              padding: "4px 8px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              backgroundColor: task.assignedTo ? "#ecfdf5" : "white",
              minWidth: "140px",
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#111827";
              e.target.style.boxShadow = "0 0 0 2px rgba(17, 24, 39, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#d1d5db";
              e.target.style.boxShadow = "none";
            }}
          >
            <option value="">Unassigned</option>
            <option value={getCurrentUserUID()}>Myself ({auth.currentUser?.displayName || auth.currentUser?.email || "Me"})</option>
            {projectMembers
              .filter(m => m.uid !== getCurrentUserUID())
              .map((member) => (
                <option key={member.uid} value={member.uid}>
                  {member.name}
                </option>
              ))}
          </select>
        </Typography>
      )}

      {/* Show Assigned To (Read-only) for non-Pending tasks */}
      {task.status !== "Pending" && task.assignedTo && (
        <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: "6px", fontSize: "0.75rem", color: "#6b7280" }}>
          Assigned to: <strong style={{ color: "#374151", fontWeight: 600 }}>{task.assignedToName || "Unknown"}</strong>
        </Typography>
      )}

      {/* Show Task Age - Skip for Completed tasks */}
      {task.status !== "Completed" && task.createdAt && calculateTaskAge(task.createdAt) && (
        <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: "6px", fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>
          Age: <strong style={{ color: "#4b5563", fontWeight: 600 }}>{calculateTaskAge(task.createdAt)}</strong>
        </Typography>
      )}

      {/* Status Change History Log */}
      {task.statusHistory && Array.isArray(task.statusHistory) && task.statusHistory.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e5e7eb", marginBottom: "4px" }}>
          <div style={{ fontWeight: 500, marginBottom: "4px", color: "#374151", fontSize: "0.75rem" }}>Status History:</div>
          <div style={{ maxHeight: "80px", overflowY: "auto", fontSize: "0.7rem", lineHeight: "1.4" }}>
            {task.statusHistory
              .slice()
              .reverse()
              .map((entry, idx) => (
                <div key={idx} style={{ marginBottom: "3px" }}>
                  <span style={{ color: "#6b7280" }}>
                    {formatTs(entry.updatedAt)} - Changed to <strong style={{ color: "#374151" }}>{entry.status}</strong> by <strong style={{ color: "#374151" }}>{entry.updatedBy || "Unknown"}</strong>
                    {entry.previousStatus && entry.previousStatus !== entry.status && (
                      <span style={{ color: "#9ca3af" }}> (from {entry.previousStatus})</span>
                    )}
                  </span>
                </div>
              ))}
          </div>
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" display="block" style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #f3f4f6" }}>
        {formatTs(task.updatedAt)}
      </Typography>
    </Paper>
  );

  if (!projectId) {
    return <div>No project selected</div>;
  }

  return (
    <div style={{ width: "100%", backgroundColor: "transparent" }}>
      {/* Filter Bar */}
      <Box
        style={{
          backgroundColor: "#ffffff",
          padding: "12px 20px",
          marginBottom: "16px",
          borderRadius: "6px",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
          alignItems: "center",
          border: "1px solid #e5e7eb",
        }}
      >
        {/* User Filter - Moved to Dashboard Header */}
        {/* Date Range Filter - Moved to Dashboard Header */}

        {/* Status Filter */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 auto", flexWrap: "wrap" }}>
          <Typography variant="body2" style={{ fontWeight: 500, color: "#6b7280", whiteSpace: "nowrap", fontSize: "0.875rem" }}>
            Show:
          </Typography>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {[
              { name: "Pending", activeBg: "#fef3c7", activeText: "#92400e", activeBorder: "#fbbf24" },
              { name: "In Progress", activeBg: "#dbeafe", activeText: "#1e40af", activeBorder: "#60a5fa" },
              { name: "On Hold", activeBg: "#fee2e2", activeText: "#991b1b", activeBorder: "#f87171" },
              { name: "Completed", activeBg: "#d1fae5", activeText: "#065f46", activeBorder: "#34d399" }
            ].map(status => (
              <button
                key={status.name}
                onClick={() => toggleStatus(status.name)}
                style={{
                  padding: "6px 14px",
                  borderRadius: "6px",
                  border: visibleStatuses.has(status.name) ? "1px solid " + status.activeBorder : "1px solid #d1d5db",
                  backgroundColor: visibleStatuses.has(status.name) ? status.activeBg : "#ffffff",
                  color: visibleStatuses.has(status.name) ? status.activeText : "#6b7280",
                  fontSize: "0.8125rem",
                  fontWeight: visibleStatuses.has(status.name) ? 600 : 400,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  whiteSpace: "nowrap"
                }}
                onMouseEnter={(e) => {
                  if (!visibleStatuses.has(status.name)) {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                    e.currentTarget.style.borderColor = "#9ca3af";
                  } else {
                    e.currentTarget.style.opacity = "0.9";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!visibleStatuses.has(status.name)) {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  } else {
                    e.currentTarget.style.opacity = "1";
                  }
                }}
              >
                {status.name}
              </button>
            ))}
          </div>
        </div>
      </Box>

      {/* Board Columns */}
      <div
        className="board-columns-container"
        style={{
          minHeight: "calc(100vh - 350px)",
          width: "100%",
          paddingBottom: "20px",
        }}
      >
        {/* Pending Column */}
        {visibleStatuses.has("Pending") && (
          <Card
            style={{
              backgroundColor: "white",
              borderRadius: "6px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 280px)",
              overflow: "hidden",
              transition: "all 0.15s ease",
              cursor: "default",
              border: "1px solid #e5e7eb",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.08)";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            <CardContent
              style={{
                backgroundColor: "#fef3c7",
                padding: "14px 18px",
                borderBottom: "1px solid #fbbf24",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <Typography variant="h6" style={{ fontWeight: 600, color: "#92400e", fontSize: "0.9375rem", marginBottom: "4px" }}>
                    Pending
                  </Typography>
                  <Typography variant="caption" style={{ color: "#a16207", fontSize: "0.75rem", fontWeight: 400 }}>
                    {pendingTasks.length} task{pendingTasks.length !== 1 ? "s" : ""}
                  </Typography>
                </div>
              </div>
            </CardContent>
            <CardContent
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "14px 16px",
                backgroundColor: "#ffffff",
              }}
            >
              {pendingTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" style={{ textAlign: "center", padding: "32px 20px", color: "#9ca3af", fontSize: "0.8125rem" }}>
                  No pending tasks
                </Typography>
              ) : (
                <div style={{ width: "100%" }}>
                  {pendingTasks.map((task) => renderTaskCard(task))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* In Progress Column */}
        {visibleStatuses.has("In Progress") && (
          <Card
            style={{
              backgroundColor: "white",
              borderRadius: "6px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 280px)",
              overflow: "hidden",
              transition: "all 0.15s ease",
              cursor: "default",
              border: "1px solid #e5e7eb",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.08)";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            <CardContent
              style={{
                backgroundColor: "#dbeafe",
                padding: "14px 18px",
                borderBottom: "1px solid #60a5fa",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <Typography variant="h6" style={{ fontWeight: 600, color: "#1e40af", fontSize: "0.9375rem", marginBottom: "4px" }}>
                    In Progress
                  </Typography>
                  <Typography variant="caption" style={{ color: "#1e3a8a", fontSize: "0.75rem", fontWeight: 400 }}>
                    {inProgressTasks.length} task{inProgressTasks.length !== 1 ? "s" : ""}
                  </Typography>
                </div>
              </div>
            </CardContent>
            <CardContent
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "14px 16px",
                backgroundColor: "#ffffff",
              }}
            >
              {inProgressTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" style={{ textAlign: "center", padding: "32px 20px", color: "#9ca3af", fontSize: "0.8125rem" }}>
                  No in-progress tasks
                </Typography>
              ) : (
                <div style={{ width: "100%" }}>
                  {inProgressTasks.map((task) => renderTaskCard(task))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* On Hold Column */}
        {visibleStatuses.has("On Hold") && (
          <Card
            style={{
              backgroundColor: "white",
              borderRadius: "6px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 280px)",
              overflow: "hidden",
              transition: "all 0.15s ease",
              cursor: "default",
              border: "1px solid #e5e7eb",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.08)";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            <CardContent
              style={{
                backgroundColor: "#fee2e2",
                padding: "14px 18px",
                borderBottom: "1px solid #f87171",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <Typography variant="h6" style={{ fontWeight: 600, color: "#991b1b", fontSize: "0.9375rem", marginBottom: "4px" }}>
                    On Hold
                  </Typography>
                  <Typography variant="caption" style={{ color: "#7f1d1d", fontSize: "0.75rem", fontWeight: 400 }}>
                    {onHoldTasks.length} task{onHoldTasks.length !== 1 ? "s" : ""}
                  </Typography>
                </div>
              </div>
            </CardContent>
            <CardContent
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "14px 16px",
                backgroundColor: "#ffffff",
              }}
            >
              {onHoldTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" style={{ textAlign: "center", padding: "32px 20px", color: "#9ca3af", fontSize: "0.8125rem" }}>
                  No on-hold tasks
                </Typography>
              ) : (
                <div style={{ width: "100%" }}>
                  {onHoldTasks.map((task) => renderTaskCard(task))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed Column */}
        {visibleStatuses.has("Completed") && (
          <Card
            style={{
              backgroundColor: "white",
              borderRadius: "6px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 280px)",
              overflow: "hidden",
              transition: "all 0.15s ease",
              cursor: "default",
              border: "1px solid #e5e7eb",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.08)";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
          >
            <CardContent
              style={{
                backgroundColor: "#d1fae5",
                padding: "14px 18px",
                borderBottom: "1px solid #34d399",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <Typography variant="h6" style={{ fontWeight: 600, color: "#065f46", fontSize: "0.9375rem", marginBottom: "4px" }}>
                    Completed
                  </Typography>
                  <Typography variant="caption" style={{ color: "#064e3b", fontSize: "0.75rem", fontWeight: 400 }}>
                    {completedTasks.length} task{completedTasks.length !== 1 ? "s" : ""}
                  </Typography>
                </div>
              </div>
            </CardContent>
            <CardContent
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "14px 16px",
                backgroundColor: "#ffffff",
              }}
            >
              {completedTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" style={{ textAlign: "center", padding: "32px 20px", color: "#9ca3af", fontSize: "0.8125rem" }}>
                  No completed tasks
                </Typography>
              ) : (
                <div style={{ width: "100%" }}>
                  {completedTasks.map((task) => renderTaskCard(task))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

